import { connect } from 'react-redux';
import React from 'react';
/** import third-party libraries in the section above, Sort Lines Ascending */

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
/** import mui components in the section above, Sort Lines Ascending */

import { DialogContent } from 'widgets-v6/modal';
/** import widgets components in the section above, Sort Lines Ascending */

import actions from './actions';
import selector from './selector';
/** import single application imports in the section above, Sort Lines Ascending */

const CLIENT_CAMPAIGN_FIELD_TYPES = new Set(['advertiser', 'partner', 'sales_rep']);

const CLIENT_ROLE_LABELS = {
    advertiser: 'advertiser',
    partner: 'partner',
    sales_rep: 'sales rep',
};

const useStyles = makeStyles(theme => ({
    dialogTitleRoot: {
        padding: `10px ${theme.spacing(1)} 10px ${theme.spacing(3)}`,
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
}));

/** Inline submit loading indicator sized to match button label height. */
const DeprecateButtonSpinner = () => <CircularProgress size={24} color="inherit" />;

/**
 * Builds confirm-modal copy from org context cached at open time (no extra network).
 *
 * @param {Object} options
 * @param {string} [options.clientType] - From organization.client_type (or type fallback)
 * @param {string} options.organizationName - From organization.name
 * @param {number} [options.userCount] - Cached user count at open; 0 means org-only consequence copy
 * @returns {{ openingConsequence: string, campaignParagraph: string, warningText: string }}
 */
function getDeprecateModalCopy({ organizationName, clientType, userCount = 0 }) {
    const name = organizationName || 'this organization';

    let openingConsequence = 'This will immediately remove the organization.';
    if (userCount > 0) {
        openingConsequence = `This will immediately remove the organization and its ${userCount} user(s).`;
    }

    let campaignParagraph;
    if (CLIENT_CAMPAIGN_FIELD_TYPES.has(clientType)) {
        const role = CLIENT_ROLE_LABELS[clientType] ?? clientType;
        campaignParagraph = `Existing campaigns using this ${role} won't be affected, but it will no longer be possible to select ${name} for new campaigns.`;
    } else {
        // media_buyer, admin, and other non-client campaign roles
        campaignParagraph = `Campaigns from ${name} won't be affected. Please check if there are live/scheduled campaigns before proceeding.`;
    }

    const warningText =
        'This can’t be undone directly, but our support team is happy to help if you need to reverse this';

    return { openingConsequence, campaignParagraph, warningText };
}

const DeprecateOrgModal = ({ dispatch, isOpen, isDeprecating, organization, userCount }) => {
    const classes = useStyles();
    const handleClose = () => dispatch(actions.close());
    const handleDeprecate = () => {
        dispatch(actions.deprecate());
    };

    const organizationName = organization?.name ?? '';
    // Prefer client_type for campaign-role copy (advertiser/partner/sales_rep)
    const clientType = organization?.client_type ?? organization?.type ?? '';
    const displayName = organizationName || 'this organization';
    const { openingConsequence, campaignParagraph, warningText } = getDeprecateModalCopy({
        organizationName,
        clientType,
        userCount,
    });

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={isOpen}
            onClose={isDeprecating ? undefined : handleClose}
        >
            <DialogTitle classes={{ root: classes.dialogTitleRoot }}>
                Deprecate Organization
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <Alert severity="error" sx={{ alignItems: 'center' }}>
                        <Typography variant="body1">{warningText}</Typography>
                    </Alert>
                    <Typography variant="body1" component="div">
                        Are you sure you want to deprecate <strong>{displayName}</strong>?
                        <br />
                        {openingConsequence}
                    </Typography>
                    <Typography variant="body1">{campaignParagraph}</Typography>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleDeprecate}
                    disabled={isDeprecating}
                >
                    {isDeprecating ? <DeprecateButtonSpinner /> : 'Deprecate'}
                </Button>
                <Button variant="outlined" onClick={handleClose} disabled={isDeprecating}>
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default connect(selector)(DeprecateOrgModal);
